<template>
  <order-item-form
    ref="form"
    :available-online="availableOnline"
    data-test-id="rewards-order-item-form"
    @change="handleChange"
    @add-model="orderItem => $emit('add-model', orderItem)"
  >
    <template #title>
      {{ (currentOrderItem && currentOrderItem.title) || 'Item' }}
    </template>
    <template #price>
      <div class="text-h4 text-responsive-grey">
        Total: <span class="text-h4 text-responsive"> {{ `${pointsCost} ${(rewardsCurrencyName || $tc(`user.point`, pointsCost) )}` }}</span>
        <template v-if="displayPrice > 0">
          + <i18n-n
            class="text-h4 text-responsive"
            tag="span"
            :value="displayPrice"
            format="currency"
          />
        </template>
      </div>
    </template>
    <template #buttonSubtitle>
      <span>
        {{ `${pointsCost} ${(rewardsCurrencyName || $tc(`user.point`, pointsCost) )}` }}
      </span>
      <template v-if="displayPrice > 0">
        + <i18n-n
          tag="span"
          :value="displayPrice"
          format="currency"
        />
      </template>
    </template>
    <template #item-detail="{ orderItem }">
      <item-detail
        v-if="currentOrderItem && currentOrderItem.type === 'item'"
        ref="itemDetail"
        :key="changeNumber"
        class="col-12 col-sm-8"
        :class="{ 'q-pa-md scroll': $q.screen.gt.xs && $isLandscapeView() }"
        :style="{ height: $q.screen.gt.xs && $isLandscapeView() ? '100%' : undefined }"
        :order-item="orderItem"
        :available-online="availableOnline"
        @request-authorization="callback => $emit('request-authorization', callback)"
        @merge-tab="foundOrder => $emit('merge-tab', foundOrder)"
      >
        <template #item-variation-buttons>
          <item-variation-buttons
            :order-item="orderItem"
            :item-order-item="orderItem.itemOrderItem"
            :item-variations="voucherItemVariations"
            :display-price="false"
            :class="{
              'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
              'q-mb-lg': !$q.screen.lt.sm && $isLandscapeView()
            }"
            @click="itemVariationChange"
            @request-authorization="callback => $emit('request-authorization', callback)"
          />
        </template>
        <template #item-modifier-sets="{ itemModifierSets }">
          <item-modifier-set
            v-for="itemModifierSet in itemModifierSets"
            :key="itemModifierSet.id"
            :order-item="orderItem"
            :item-order-item="orderItem.itemOrderItem"
            :item-modifier-set="itemModifierSet"
            :available-online="availableOnline"
            :class="{
              'border-bottom q-pa-md': $q.screen.lt.sm || !$isLandscapeView(),
              'q-mb-lg': !$q.screen.lt.sm && $isLandscapeView()
            }"
            @request-authorization="callback => $emit('request-authorization', callback)"
          >
            <template
              #price="{ modifier }"
            >
              <div
                v-if="modifier.price && calculateExtraCost(modifier)"
              >
                <i18n-n
                  :value="calculateExtraCost(modifier)"
                  format="currency"
                />
              </div>
              <div
                v-else
              >
                <div />
              </div>
            </template>
          </item-modifier-set>
        </template>
      </item-detail>
    </template>
  </order-item-form>
</template>
<script>
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import ItemDetail from 'src/modules/register/components/order-item-form/components/ItemDetail'
import ItemModifierSet from 'src/modules/register/components/order-item-form/components/ItemModifierSet'
import ItemVariationButtons from 'src/modules/register/components/order-item-form/components/ItemVariationButtons'
import OrderItemForm from 'src/modules/register/components/order-item-form/OrderItemForm'

export default {
  name: 'RewardsOrderItemForm',
  components: {
    ItemDetail,
    ItemModifierSet,
    ItemVariationButtons,
    OrderItemForm
  },
  props: {
    rewardsCurrencyName: {
      type: String,
      required: false,
      default: ''
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['request-authorization', 'merge-tab', 'add-model'],
  data () {
    return {
      pointsCost: 0,
      currentOrderItem: null,
      voucherItemVariations: [],
      changeNumber: 0,
      itemVariationVoucherPairs: {},
      voucherTypes: [],
      currentVariation: null,
      currentOrderItemOrder: null
    }
  },
  computed: {
    orderItemFormItem () {
      return this.$refs.form.orderItem
    },
    itemPriceWithoutRedemptions () {
      return this.orderItemFormItem.displayPrice
    },
    displayPrice () {
      return this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice
    }
  },
  watch: {
    'currentOrderItem.id': {
      handler () {
        this.itemVariationVoucherPairs = {}
        this.voucherItemVariations = []
        if (!this.currentOrderItem) {
          return
        }
        this.voucherItemVariations = this.getVariationsAndVouchers()
      }
    }
  },
  methods: {
    show (orderItem, pointsCost, voucherTypes, order) {
      this.pointsCost = pointsCost
      this.currentOrderItem = orderItem
      this.voucherTypes = voucherTypes
      this.currentOrderItemOrder = order
      this.$refs.form.setCollection(order.orderItems)
      this.$refs.form.show(orderItem)
    },
    handleChange () {
      this.currentOrderItemOrder.applyReward(this.currentVoucher)
      this.currentOrderItemOrder.applyRedemptions()
    },
    calculateExtraCost (modifier) {
      // We display all variations that have a price less than or equal to the max price of the voucher
      // but a modifier could exceed this price when added. If it does, we need to display the extra cost.
      const voucherDoesNotCoverItemWithModifiers = this.currentVoucher.hasMaxPrice && this.currentVoucher.maxPrice < (modifier.price + this.itemPriceWithoutRedemptions)
      const modifierIsSelected = this.orderItemFormItem.itemOrderItem.hasModifier(modifier)
      if (voucherDoesNotCoverItemWithModifiers) {
        if (modifierIsSelected) {
          return this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice > 0 ? (modifier.price < this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice ? modifier.price : this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice) : null
        } else {
          return modifier.price < modifier.price + this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice ? modifier.price : modifier.price + this.itemPriceWithoutRedemptions - this.currentVoucher.maxPrice
        }
      }
      return null
    },
    itemVariationChange (itemVariation) {
      this.currentVoucher = this.itemVariationVoucherPairs[itemVariation.id]
      this.itemVariationPrice = itemVariation.price
      this.currentVariation = this.itemVariation
      // tells the item detail component to reload so that calculateExtraCost runs for the newly selected variation
      this.changeNumber++
    },
    getVariationsAndVouchers () {
      // Need to find and return all variations for selected item that are covered by vouchers at this points cost.
      // Because different variations could be covered by different vouchers, we create a hash of variationIds and vouchers
      // while we're at it.
      const applicableVariations = []
      _.each(this.currentOrderItem.itemOrderItem.item.itemVariations.data, itemVariation => {
        const voucher = _.find(this.voucherTypes, voucherType => (
          (voucherType.type === ORDER_ITEM_TYPES.ITEM &&
           voucherType.isItem(this.currentOrderItem.itemOrderItem.item)) || voucherType.itemVariationId === itemVariation.id
        ) && (
          !voucherType.hasMaxPrice || voucherType.maxPrice >= itemVariation.price
        ))
        if (voucher) {
          this.itemVariationVoucherPairs[itemVariation.id] = voucher
          applicableVariations.push(itemVariation)
        }
      })
      this.currentVariation = this.currentOrderItem.itemOrderItem.itemVariation || applicableVariations[0]
      this.currentVoucher = this.itemVariationVoucherPairs[this.currentVariation.id]
      return applicableVariations
    }
  }
}
</script>
